import { getErrorStatus, toError } from '~/utils/http/api-error'

export type CancelOrderStatus = 'idle' | 'submitting' | 'success' | 'error' | 'systemError'

/**
 * EU click-to-cancel request, submitted from the footer's CancelOrderPopup.
 *
 * A 404 means the backend found no order for the email — an expected, user-correctable
 * outcome, so it maps to `error` and is not reported. Anything else (5xx, 4xx, network)
 * maps to `systemError` and is tracked. Ported from Nuxt2 `composables/useCancelOrder.ts`.
 */
export const useCancelOrder = () => {
  const { $appInsights } = useNuxtApp()
  const status = ref<CancelOrderStatus>('idle')

  const submit = async (email: string): Promise<void> => {
    status.value = 'submitting'
    try {
      await $api()('cancel-order/request', {
        method: 'POST',
        body: { email }
      })
      status.value = 'success'
    } catch (err: unknown) {
      // `$api` failures expose their HTTP status on FetchError.statusCode.
      const code = getErrorStatus(err)
      if (code !== 404) {
        $appInsights?.trackException({ exception: toError(err) })
      }
      status.value = code === 404 ? 'error' : 'systemError'
    }
  }

  const reset = (): void => {
    status.value = 'idle'
  }

  return { status, submit, reset }
}
